/**
 * Shared speed of every endless strip or ring, from hung-hola's assets/js/loop-motion.js (owner, 2026-10-20):
 * testimonials, awards, partners, team (not discover: it keeps its own speed, owner 2026-10-20).
 * - The pointer over a strip slows it down (MOTION.loop.hover), it never stops.
 * - Scrolling the page (either way) adds the scroll speed to the strip's own direction; when the scroll stops the strip
 *   glides back to its own speed. Never backwards: some strips do not wrap until they first cover their edge (discover).
 * - `follow` (partners): the scroll direction counts instead: down pushes the strip its own way, up pushes it backwards
 *   (a negative scale); still, it glides back to its own way. Only for loops that run either way.
 * - `scroll: false` (testimonials, owner 2026-10-21): the page scroll does not touch the strip, only the pointer slows it.
 * One registry for the whole page, kept on window.pxLoop, so every block bundle that imports this file shares it.
 *
 * Usage in a block script, after building its endless tween:
 *   loopMotion()?.add({ hover: listElement, apply: (scale) => tween.timeScale(scale) });
 * A loop that moves itself every frame multiplies its step by the returned loop's `scale` instead of giving `apply`.
 */
import { MOTION } from './motion';

export interface Loop {
	scale: number;
	hover: boolean;
	follow: boolean;
	scroll: boolean;
	apply: (scale: number) => void;
}

export interface LoopMotion {
	add: (options: {
		hover?: Element | Element[] | null;
		follow?: boolean;
		scroll?: boolean;
		apply?: (scale: number) => void;
	}) => Loop;
}

function createLoopMotion(): LoopMotion | undefined {
	const { gsap } = window;
	if (!gsap) {
		return undefined;
	}
	const loops = new Set<Loop>();
	let lastY = window.scrollY;

	gsap.ticker.add((time, deltaMs) => {
		const dt = Math.max(deltaMs, 1) / 1000;
		const y = window.scrollY;
		const velocity = (y - lastY) / dt; // px/s, + down, - up
		lastY = y;
		const { hover, scroll, maxBoost, settle } = MOTION.loop;
		const boost = Math.min(maxBoost, Math.abs(velocity) * scroll);
		const k = 1 - Math.pow(1 - settle, dt * 60); // same glide at any frame rate
		loops.forEach((loop) => {
			const speed = (loop.hover ? hover : 1) + (loop.scroll ? boost : 0);
			const target = loop.follow && velocity < 0 ? -speed : speed;
			loop.scale += (target - loop.scale) * k;
			loop.apply(loop.scale);
		});
	});

	return {
		add: ({ hover, follow = false, scroll = true, apply = () => {} }) => {
			const loop: Loop = { scale: 1, hover: false, follow, scroll, apply };
			[hover]
				.flat()
				.filter((el): el is Element => !!el)
				.forEach((el) => {
					el.addEventListener('pointerenter', (e) => {
						if ((e as PointerEvent).pointerType === 'mouse') {
							loop.hover = true;
						}
					});
					el.addEventListener('pointerleave', () => (loop.hover = false));
				});
			loops.add(loop);
			return loop;
		},
	};
}

/** The page's registry (made on first use); undefined without GSAP. */
export function loopMotion(): LoopMotion | undefined {
	window.pxLoop ??= createLoopMotion();
	return window.pxLoop;
}
